import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate, useLocation } from "react-router-dom";
import jsQR from "jsqr";
import { api, formatApiError } from "@/lib/api";
import { useAuth, canWrite } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import AssetImage from "@/components/AssetImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { QrCode, Spinner, MapPin, Gauge, ArrowsLeftRight, Tag, Camera, FloppyDisk, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Scan() {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [locations, setLocations] = useState([]);
  const [statuses, setStatuses] = useState(["Available", "In Service", "Sold", "Pending", "Missing"]);
  const [loading, setLoading] = useState(!!token);
  const [notFound, setNotFound] = useState(false);
  const [code, setCode] = useState("");
  const [moveLoc, setMoveLoc] = useState("");
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const extractToken = (text) => {
    const t = (text || "").trim();
    const idx = t.indexOf("/scan/");
    return idx >= 0 ? t.slice(idx + 6).split(/[?#/]/)[0] : t;
  };

  const openScanned = async (text) => {
    const t = (text || "").trim();
    if (!t) return;
    if (t.includes("/scan/")) { navigate(`/scan/${extractToken(t)}`); return; }
    if (!/:\/\//.test(t) && !t.includes(".")) { navigate(`/scan/${t}`); return; }
    try {
      const { data } = await api.get("/assets/resolve", { params: { q: t } });
      navigate(`/scan/${data.token}`);
    } catch (_) {
      toast.error("That QR code doesn't match an asset in your inventory.");
    }
  };

  const scanFile = async (file) => {
    if (!file) return;
    try {
      const url = URL.createObjectURL(file);
      const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, 1200 / Math.max(img.width, img.height));
      canvas.width = Math.round(img.width * scale); canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      const d = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const text = jsQR(d.data, d.width, d.height)?.data;
      if (!text) throw new Error("none");
      await openScanned(text);
    } catch (_) {
      toast.error("No QR code found in that photo. Try again with the label centered and in focus.");
    }
  };


  const write = user && user !== false ? canWrite(user.role) : false;

  useEffect(() => {
    if (!user || !token) return;
    setLoading(true); setNotFound(false);
    api.get(`/assets/by-token/${token}`)
      .then(({ data }) => { setAsset(data); setStatus(data.status); setMoveLoc(data.location_id || ""); setNote(data.notes || ""); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api.get("/locations").then(({ data }) => setLocations(data)).catch(() => {});
    api.get("/company").then(({ data }) => setStatuses(data.statuses || statuses)).catch(() => {});
    // eslint-disable-next-line
  }, [token, user]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Spinner size={32} className="animate-spin text-emerald-600" /></div>;
  if (user === false) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;

  const saveAll = async () => {
    setBusy(true);
    try {
      if (moveLoc && moveLoc !== asset.location_id) await api.post(`/assets/${asset.id}/move`, { to_location_id: moveLoc, reason: "Quick scan update" });
      if (status && status !== asset.status) await api.post(`/assets/${asset.id}/status`, { status });
      if (note !== (asset.notes || "")) await api.post(`/assets/${asset.id}/note`, { notes: note });
      toast.success("Saved");
      const { data } = await api.get(`/assets/${asset.id}`);
      setAsset(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/assets/${asset.id}/upload?kind=photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      const { data } = await api.get(`/assets/${asset.id}`);
      setAsset(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // No token — prompt to scan with camera or enter code
  if (!token) {
    return (
      <div className="max-w-md mx-auto py-8" data-testid="scan-prompt">
        <div className="text-center mb-6">
          <div className="h-16 w-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4"><QrCode size={32} weight="bold" className="text-emerald-600" /></div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Scan an Asset</h1>
          <p className="text-zinc-500 text-sm mt-1">Snap a photo of a printed QR label to open the asset instantly.</p>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white p-5">
          <label className="block">
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { scanFile(e.target.files[0]); e.target.value = ""; }} data-testid="scan-file-input" />
            <div className="w-full h-14 rounded-md bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center gap-2 text-base font-semibold text-white cursor-pointer"><Camera size={22} weight="bold" /> Scan from Photo</div>
          </label>
          <p className="text-xs text-zinc-500 text-center mt-2">Snap a picture of the QR label — it opens the asset automatically.</p>
        </div>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-zinc-200" /><span className="text-xs text-zinc-400 uppercase tracking-wider">or</span><div className="flex-1 h-px bg-zinc-200" />
        </div>

        <form className="rounded-lg border border-zinc-200 bg-white p-5 space-y-3" onSubmit={(e) => { e.preventDefault(); openScanned(code); }}>
          <Label className="text-sm font-semibold">Enter asset QR token</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Paste token from QR URL" className="h-12 focus:ring-2 focus:ring-emerald-500" data-testid="scan-code-input" />
          <Button type="submit" variant="outline" className="w-full h-12 border-zinc-300 font-semibold" data-testid="scan-go">Open Asset</Button>
        </form>
      </div>
    );
  }

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Spinner size={32} className="animate-spin text-emerald-600" /></div>;

  if (notFound) return (
    <div className="max-w-md mx-auto py-16 text-center" data-testid="scan-notfound">
      <QrCode size={40} className="text-zinc-300 mx-auto mb-3" />
      <p className="font-semibold">Asset not found</p>
      <p className="text-sm text-zinc-500">This QR code doesn't match any asset in your company.</p>
      <Button onClick={() => navigate("/app/inventory")} variant="outline" className="mt-5 h-11 border-zinc-300">Go to Inventory</Button>
    </div>
  );

  const title = asset.title || asset.asset_id;
  const isVehicle = (asset.asset_type || "vehicle") === "vehicle";

  return (
    <div className="max-w-md mx-auto space-y-4 pb-6" data-testid="scan-asset">
      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <AssetImage path={asset.photos?.[0]?.storage_path} className="w-full h-48 object-cover" fallbackClassName="w-full h-48" />
        <div className="p-4">
          <div className="flex items-center justify-between gap-2">
            <h1 className="font-display text-xl font-bold tracking-tight">{title}</h1>
            <StatusBadge status={asset.status} />
          </div>
          <p className="text-sm text-zinc-500 mt-0.5">{asset.asset_id} · Stock {asset.stock_number || "—"}</p>
          <div className="flex gap-4 mt-3 text-sm">
            <span className="flex items-center gap-1.5 text-zinc-600"><MapPin size={16} className="text-emerald-600" /> {asset.location_name || "Unassigned"}</span>
            {isVehicle ? (
              <span className="flex items-center gap-1.5 text-zinc-600"><Gauge size={16} className="text-emerald-600" /> {asset.mileage || "—"}</span>
            ) : asset.identifier ? (
              <span className="flex items-center gap-1.5 text-zinc-600 font-mono text-xs">{asset.identifier}</span>
            ) : null}
          </div>
          <Button onClick={() => navigate(`/app/assets/${asset.id}`)} variant="ghost" className="mt-2 h-9 px-0 text-emerald-600 gap-1.5" data-testid="scan-full-profile"><ArrowSquareOut size={16} /> View full profile</Button>
        </div>
      </div>

      {!write ? (
        <p className="text-sm text-zinc-500 text-center py-4">You have read-only access.</p>
      ) : (
        <>
          <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-4">
            <div>
              <Label className="text-sm font-semibold flex items-center gap-1.5"><ArrowsLeftRight size={16} className="text-emerald-600" /> Location</Label>
              <Select value={moveLoc} onValueChange={setMoveLoc}>
                <SelectTrigger className="h-12 mt-1.5" data-testid="scan-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold flex items-center gap-1.5"><Tag size={16} className="text-emerald-600" /> Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-12 mt-1.5" data-testid="scan-status"><SelectValue /></SelectTrigger>
                <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold">Note</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="scan-note" />
            </div>
          </div>

          <label className="block">
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} data-testid="scan-photo-input" />
            <div className="rounded-lg border border-zinc-300 border-dashed bg-white p-4 flex items-center justify-center gap-2 text-sm font-semibold text-zinc-600 cursor-pointer h-14"><Camera size={20} className="text-emerald-600" /> Take / Upload Photo</div>
          </label>

          <Button onClick={saveAll} disabled={busy} className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-base gap-2 sticky bottom-20 lg:bottom-4 shadow-lg" data-testid="scan-save">
            {busy ? <Spinner size={22} className="animate-spin" /> : <><FloppyDisk size={22} weight="bold" /> Save</>}
          </Button>
        </>
      )}
    </div>
  );
}
